import { useEffect, useState } from 'react';
import { ModalCrearNota } from '../components/ModalCrearNota';
import { ModalGenerarRelacion } from '../components/ModalGenerarRelacion';
import { CardNota } from '../components/CardNota';
import { notasApi } from '../api/notas';
import { clientesApi, empleadosApi, serviciosApi } from '../api/endpoints';
import { useAuthStore } from '../auth/authStore';
import { Spinner } from '../components/ui/Spinner';
import { Alert } from '../components/ui/Alert';
import { SearchInput } from '../components/ui/SearchInput';
import { Plus, RefreshCw, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

function toDateInputValue(d) {
  if (!d) return '';
  const date = new Date(d);
  return date.toISOString().slice(0, 10);
}

function getWeekRange() {
  const now = new Date();
  const day = now.getDay();
  const monday = new Date(now);
  monday.setDate(now.getDate() - ((day + 6) % 7));
  monday.setHours(0, 0, 0, 0);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);
  return { desde: monday, hasta: sunday };
}

export function SoporteDiario() {
  const usuario = useAuthStore((s) => s.usuario);
  const isAdmin = usuario?.rol === 'Admin';
  const navigate = useNavigate();

  const [notas, setNotas] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [clienteId, setClienteId] = useState('');
  const [rango, setRango] = useState(getWeekRange());
  const [search, setSearch] = useState('');
  const [error, setError] = useState(null);
  const [crearOpen, setCrearOpen] = useState(false);
  const [generarOpen, setGenerarOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const cs = await clientesApi.list();
        setClientes(cs);
        if (cs.length > 0) setClienteId((prev) => prev || cs[0].id);
      } catch (e) {
        setError(e?.response?.data?.error || 'Error al cargar clientes');
      }
    })();
    (async () => {
      try {
        const es = await empleadosApi.list();
        setEmpleados(es);
      } catch (e) {}
    })();
    (async () => {
      try {
        const ss = await serviciosApi.list();
        setServicios(ss);
      } catch (e) {}
    })();
  }, []);

  useEffect(() => {
    if (!clienteId) return;
    cargar();
  }, [clienteId, rango.desde, rango.hasta]);

  async function cargar() {
    setError(null);
    try {
      const data = await notasApi.listar(clienteId, rango.desde, rango.hasta);
      setNotas(data);
    } catch (e) {
      setError(e?.response?.data?.error || 'Error al cargar notas');
    }
  }

  const filtered = (notas ?? []).filter((n) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (n.descripcion || '').toLowerCase().includes(q) ||
      (n.categoria || '').toLowerCase().includes(q) ||
      (n.servicioNombre || '').toLowerCase().includes(q) ||
      (n.nombreServicio || '').toLowerCase().includes(q) ||
      (n.empleadoNombre || '').toLowerCase().includes(q)
    );
  });

  const disponibles = filtered.filter((n) => !n.procesadaEnRelacion);
  const cerradas = filtered.filter((n) => n.procesadaEnRelacion);

  return (
    <div className="p-3 sm:p-6 space-y-4">
      <header className="flex items-center gap-3 flex-wrap">
        <h1 className="text-2xl font-bold text-white">Soporte Diario</h1>
        <span className="text-sm text-muted">Notas operativas y consolidación</span>
      </header>

      <div className="bg-panel border border-border rounded-xl p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-300 mb-1.5 tracking-wide">Cliente</label>
            <select
              value={clienteId}
              onChange={(e) => setClienteId(Number(e.target.value))}
              className="w-full px-3 py-2 bg-panel-2 border border-border rounded-md text-sm text-white"
            >
              {clientes.map((c) => (
                <option key={c.id} value={c.id}>{c.nombreComercial}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-300 mb-1.5 tracking-wide">Desde</label>
            <input
              type="date"
              value={toDateInputValue(rango.desde)}
              onChange={(e) => setRango({ ...rango, desde: e.target.value ? new Date(e.target.value) : null })}
              className="w-full px-3 py-2 bg-panel-2 border border-border rounded-md text-sm text-white"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-300 mb-1.5 tracking-wide">Hasta</label>
            <input
              type="date"
              value={toDateInputValue(rango.hasta)}
              onChange={(e) => setRango({ ...rango, hasta: e.target.value ? new Date(e.target.value) : null })}
              className="w-full px-3 py-2 bg-panel-2 border border-border rounded-md text-sm text-white"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-300 mb-1.5 tracking-wide">Buscar</label>
            <SearchInput value={search} onChange={setSearch} placeholder="descripción, categoría..." />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCrearOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white text-sm font-semibold rounded-md"
          >
            <Plus size={16} /> Nueva nota
          </button>
          <button
            onClick={() => setGenerarOpen(true)}
            disabled={disponibles.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold rounded-md disabled:opacity-30"
          >
            <FileText size={16} /> Generar relación ({disponibles.length})
          </button>
          <button
            onClick={cargar}
            className="flex items-center gap-2 px-3 py-2 bg-panel-2 hover:bg-border text-slate-300 text-sm rounded-md ml-auto"
          >
            <RefreshCw size={14} /> Refrescar
          </button>
        </div>
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      {notas === null ? (
        <div className="flex justify-center py-12">
          <Spinner size={32} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-muted text-sm py-8">
          No hay notas en el rango seleccionado.
        </div>
      ) : (
        <>
          {disponibles.length > 0 && (
            <section>
              <h2 className="text-sm font-semibold text-slate-300 tracking-wide mb-2">
                Disponibles ({disponibles.length})
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {disponibles.map((n) => (
                  <CardNota
                    key={n.id}
                    nota={n}
                    empleados={empleados}
                    servicios={servicios}
                    onChanged={cargar}
                  />
                ))}
              </div>
            </section>
          )}
          {cerradas.length > 0 && (
            <section>
              <h2 className="text-sm font-semibold text-slate-500 tracking-wide mb-2 mt-4">
                Procesadas ({cerradas.length})
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {cerradas.map((n) => (
                  <CardNota
                    key={n.id}
                    nota={n}
                    empleados={empleados}
                    servicios={servicios}
                    onChanged={cargar}
                  />
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {crearOpen && (
        <ModalCrearNota
          isOpen={crearOpen}
          onClose={() => setCrearOpen(false)}
          onSaved={async () => {
            setCrearOpen(false);
            await cargar();
          }}
          clienteId={clienteId}
          clienteNombre={clientes.find((c) => c.id === clienteId)?.nombreComercial || ''}
          empleados={empleados}
          servicios={servicios}
        />
      )}

      {generarOpen && (
        <ModalGenerarRelacion
          isOpen={generarOpen}
          onClose={() => setGenerarOpen(false)}
          onSaved={async (relId) => {
            setGenerarOpen(false);
            await cargar();
            navigate(`/clientes/${clienteId}/relaciones/${relId}`);
          }}
          clienteId={clienteId}
          notas={disponibles}
        />
      )}
    </div>
  );
}
